import React, { useState } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { AttachMoney, LocationOn, Timer, Description, Person } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapComponent } from '../components/MapComponent';
import { useLocation } from 'react-router-dom';
import { compressImage } from '../utils/imageCompressor';
import { useStores } from '../hooks/useStores';
import { Store } from '../types';
import { storeService } from '../services/storeService';




type LoadScriptProps = {
  googleMapsApiKey: string;
  libraries?: string[];
};

type GoogleMapProps = {
  mapContainerStyle: {
    width: string | number;
    height: string | number;
  };
  center: {
    lat: number;
    lng: number;
  };
  zoom: number;
};

type MarkerProps = {
  position: {
    lat: number;
    lng: number;
  };
};

interface DashboardProps {}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const { stores, isLoading, error } = useStores();

  // Stores are already fetched via useStores hook

  const handleClose = () => {
    setOpen(false);
    setSelectedStore(null);
  };

  const handleOpen = (store: Store) => {
    setSelectedStore(store);
    setOpen(true);
  };

  const handleAccept = async (storeId: string) => {
    // TODO: Implement accept logic
    handleClose();
  };

  const checkInSchema = yup.object<CheckInFormValues>().shape({
    otherProducts: yup.string().required('This field is required'),
    fridgeCondition: yup.string().required('This field is required'),
    exteriorSign: yup.string().required('This field is required'),
    interiorSigns: yup.string().required('This field is required'),
    comments: yup.string().required('This field is required'),
    photos: yup.array().of(yup.string()).default([]),
  });

  type CheckInFormValues = {
    otherProducts: string;
    fridgeCondition: string;
    exteriorSign: string;
    interiorSigns: string;
    comments: string;
    photos: (string | undefined)[];
  };

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<CheckInFormValues>({
    resolver: yupResolver(checkInSchema),
    defaultValues: {
      otherProducts: 'na',
      fridgeCondition: 'na',
      exteriorSign: 'na',
      interiorSigns: 'na',
      comments: '',
      photos: []
    },
  });

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const compressedPhotos = await Promise.all(
      Array.from(files).map(async (file) => {
        const compressed = await compressImage(file, { maxWidth: 800, quality: 0.8 });
        return URL.createObjectURL(compressed);
      })
    );

    setValue('photos', compressedPhotos.slice(0, 4)); // Limit to 4 photos
  };

  const submitCheckInMutation = useMutation({
    mutationFn: async (data: any) => {
      // TODO: Implement actual API call to submit check-in
      console.log('Submitting check-in:', data);
      return { success: true };
    },
    onSuccess: () => {
      handleClose();
      // TODO: Update store status to 'completed'
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitCheckInMutation.mutateAsync(data);
    } catch (error) {
      console.error('Error submitting check-in:', error);
    }
  });

  const renderCheckInForm = () => {
    if (selectedStore?.status !== 'accepted') return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Check-in Questions
        </Typography>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Are there other products in the Fernandes fridge other than Fernandes branded products?</FormLabel>
          <RadioGroup
            row
            {...register('otherProducts')}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
            <FormControlLabel value="na" control={<Radio />} label="Not Applicable" />
            <FormControlLabel value="other" control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Are the fridges in working condition and are they clean?</FormLabel>
          <RadioGroup
            row
            {...register('fridgeCondition')}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
            <FormControlLabel value="na" control={<Radio />} label="Not Applicable" />
            <FormControlLabel value="other" control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Is there a permanent sign that clearly says 'Fernandes' on the exterior of the store?</FormLabel>
          <RadioGroup
            row
            {...register('exteriorSign')}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
            <FormControlLabel value="na" control={<Radio />} label="Not Applicable" />
            <FormControlLabel value="other" control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Are there any other prominent signs or promotional materials inside the store that say 'Fernandes'?</FormLabel>
          <RadioGroup
            row
            {...register('interiorSigns')}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
            <FormControlLabel value="na" control={<Radio />} label="Not Applicable" />
            <FormControlLabel value="other" control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Additional Comments (Optional)"
          {...register('comments')}
          sx={{ mb: 2 }}
        />

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
          >
            Upload Photos
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
            />
          </Button>
          <Typography variant="caption" color="text.secondary">
            Upload up to 4 photos
          </Typography>
        </Stack>


      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // Categorize stores by status
  const openStores = stores?.filter(store => store.status === 'open') || [];
  const acceptedStores = stores?.filter(store => store.status === 'accepted') || [];
  const waitingApprovalStores = stores?.filter(store => store.status === 'waiting_for_approval') || [];
  const completedStores = stores?.filter(store => store.status === 'completed') || [];

  const renderStoreCard = (store: Store) => (
    <Grid item xs={12} sm={6} md={4} key={store.id}>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
          },
        }}
        onClick={() => handleOpen(store)}
      >
        {/* Header with bottles image */}
        <Box 
          sx={{
            height: 120,
            backgroundImage: 'url(/images/bottles.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }
          }}
        >
          <Typography 
            variant="h5" 
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              color: 'white',
              fontWeight: 600,
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}
          >
            {store.name}
          </Typography>
        </Box>
        
        {/* Store details */}
        <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {store.address}
          </Typography>
          <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#025B29', fontWeight: 600 }}>
              ${store.payout}
            </Typography>
            {store.distance !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {Math.round(store.distance * 10) / 10} mi
                </Typography>
              </Box>
            )}
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1,
              color: store.status === 'accepted' 
                ? 'success.main' 
                : store.status === 'waiting_for_approval'
                ? 'warning.main'
                : 'primary.main',
              fontWeight: 500
            }}
          >
            Status: {store.status === 'waiting_for_approval' 
              ? 'Waiting for Approval' 
              : store.status.charAt(0).toUpperCase() + store.status.slice(1)}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600, color: '#025B29' }}>
        Available Check-ins
      </Typography>
      
      {/* Open Stores */}
      {openStores.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0, // Height of the app bar
              zIndex: 10,
              bgcolor: 'background.paper',
              py: 2,
              mb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Open • {openStores.length} store{openStores.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {openStores.map(store => renderStoreCard(store))}
          </Grid>
        </Box>
      )}
      
      {/* Accepted Stores */}
      {acceptedStores.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0, // Height of the app bar
              zIndex: 10,
              bgcolor: 'background.paper',
              py: 2,
              mb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
              In Progress • {acceptedStores.length} store{acceptedStores.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {acceptedStores.map(store => renderStoreCard(store))}
          </Grid>
        </Box>
      )}
      
      {/* Waiting for Approval Stores */}
      {waitingApprovalStores.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0, // Height of the app bar
              zIndex: 10,
              bgcolor: 'background.paper',
              py: 2,
              mb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
              Waiting for Approval • {waitingApprovalStores.length} store{waitingApprovalStores.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {waitingApprovalStores.map(store => renderStoreCard(store))}
          </Grid>
        </Box>
      )}
      
      {/* Completed Stores */}
      {completedStores.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0, // Height of the app bar
              zIndex: 10,
              bgcolor: 'background.paper',
              py: 2,
              mb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Completed • {completedStores.length} store{completedStores.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {completedStores.map(store => renderStoreCard(store))}
          </Grid>
        </Box>
      )}
      
      {/* No stores message */}
      {stores?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No stores available for check-in at the moment.
          </Typography>
        </Box>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            margin: '32px',
            '@media (max-width: 600px)': {
              margin: '16px',
              width: 'calc(100% - 32px)',
            },
            '& .MuiDialogContent-root': {
              overflowY: 'auto',
              padding: '0 24px 24px',
              '&:first-of-type': {
                paddingTop: '24px'
              }
            }
          }
        }}
      >
        <DialogTitle>{selectedStore?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ height: '400px', width: '100%', borderRadius: 1, overflow: 'hidden' }}>
            {selectedStore && (
              <MapComponent
                latitude={selectedStore.latitude}
                longitude={selectedStore.longitude}
                name={selectedStore.name}
                address={selectedStore.address}
              />
            )}
          </Box>
          {selectedStore?.status === 'accepted' ? (
            renderCheckInForm()
          ) : (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Store Details
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney color="primary" />
                    <Typography variant="h5" color="primary">
                      ${selectedStore?.payout}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per check-in
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {selectedStore?.address}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  {selectedStore?.distance !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Timer color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(selectedStore.distance)} miles away
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Required Tasks
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Description color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Take photos of the store"
                        secondary="Capture the exterior and interior"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Person color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Verify store hours"
                        secondary="Check and confirm opening times"
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedStore?.status === 'accepted' ? (
            <>
              <Button onClick={handleClose}>Close</Button>
              <Button
                onClick={onSubmit}
                variant="contained"
                color="primary"
                disabled={submitCheckInMutation.isPending}
              >
                {submitCheckInMutation.isPending ? 'Submitting...' : 'Submit Check-in'}
              </Button>
            </>
          ) : selectedStore?.status === 'open' ? (
            <Button onClick={() => handleAccept(selectedStore?.id || '')}>Accept</Button>
          ) : (
            <Button onClick={handleClose}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
